import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { attachId, attachIds } from '../utils';

import {
  Attempt, Exam, ExamAttempt, ExamMarkingRequest, ExamType, Question, ShortExam,
} from './types';

export const examsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getExams: build.query<CognitoAPIResponse<ShortExam[]>, {
      subject: string,
      includeInactive?: boolean,
    }>({
      query(arg) {
        const { subject, includeInactive } = arg;

        return {
          url: `exams/${subject}`,
          method: 'GET',
          params: {
            includeInactive,
          },
        };
      },
      providesTags: (result, error, arg) =>
        (result
          ? [...result.data.map(({ id } : { id: string }) =>
            ({
              type: 'Exams' as const,
              id: `${arg.subject}#${id}`,
            })), 'Exams']
          : ['Exams']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.examId);
      },
    }),
    getExam: build.query<CognitoAPIResponse<Exam>, {
      subject: string,
      examId: string,
    }>({
      query(arg) {
        const { subject, examId } = arg;

        return {
          url: `exams/${subject}/${examId}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) =>
        (result
          ? [...result.data.questions.map(({ questionId } : { questionId: string }) =>
            ({
              type: 'ExamQuestions' as const,
              id: `${arg.subject}#${arg.examId}#${questionId}`,
            })), 'ExamQuestions',
          {
            type: 'Exams', id: `${arg.subject}#${arg.examId}`,
          }]
          : [
            { type: 'ExamQuestions' },
            {
              type: 'Exams', id: `${arg.subject}#${arg.examId}`,
            },
          ]),
      transformResponse(response: any) {
        return attachId(response, (entry: any) => `${entry.subject}#${entry.examId}`);
      },
    }),
    getQuestion: build.query<CognitoAPIResponse<Question>, {
      subject: string,
      examId: string,
      questionId: string,
    }>({
      query(arg) {
        const { subject, examId, questionId } = arg;

        return {
          url: `exams/${subject}/${examId}/${questionId}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'ExamQuestions', id: `${arg.subject}#${arg.examId}#${arg.questionId}`,
      }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) => `${entry.subject}#${entry.examId}#${entry.questionId}`);
      },
    }),
    newExam: build.mutation<any, {
      subject: string, label: string, shortLabel: string, type: string
    }>({
      query(arg) {
        const { subject, label, shortLabel, type } = arg;

        return {
          url: 'exams/new',
          body: {
            subject, label, shortLabel, type,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['Exams'],
    }),
    updateExam: build.mutation<any, { subject: string, examId: string, attributes: any }>({
      query(arg) {
        const { subject, examId, attributes } = arg;

        return {
          url: `exams/${subject}/${examId}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'Exams',
        id: `${arg.subject}#${arg.examId}`,
      }],
    }),
    updateQuestion: build.mutation<any, {
      subject: string,
      examId: string,
      questionId: string,
      attributes: any
    }>({
      query(arg) {
        const { subject, examId, questionId, attributes } = arg;

        return {
          url: `exams/${subject}/${examId}/${questionId}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamQuestions',
        id: `${arg.subject}#${arg.examId}#${arg.questionId}`,
      }, {
        type: 'Exams',
        id: `${arg.subject}#${arg.examId}`,
      }],
    }),
    addQuestion: build.mutation<any, { subject: string, examId: string, attributes: any }>({
      query(arg) {
        const { subject, examId, attributes } = arg;

        return {
          url: `exams/${subject}/${examId}/question/add`,
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamQuestions',
        id: `${arg.subject}#${arg.examId}#${result?.data?.id}`,
      },
      {
        type: 'Exams',
        id: `${arg.subject}#${arg.examId}`,
      }],
    }),
    removeQuestion: build.mutation<any, { subject: string, examId: string, questionId: string }>({
      query(arg) {
        const { subject, examId, questionId } = arg;

        return {
          url: `exams/${subject}/${examId}/${questionId}/remove`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamQuestions',
        id: `${arg.subject}#${arg.examId}#${arg.questionId}`,
      },
      {
        type: 'Exams',
        id: `${arg.subject}#${arg.examId}`,
      }],
    }),
    deleteExam: build.mutation<any, { subject: string, examId: string }>({
      query(arg) {
        const { subject, examId } = arg;

        return {
          url: `exams/${subject}/${examId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Exams', 'ExamQuestions'],
    }),
    markQuestion: build.mutation<any,
    { subject: string,
      examId: string,
      questionId: string,
      attemptNum: number,
      studentId: string,
      attemptData: Attempt }>({
      query(arg) {
        const { subject, examId, questionId, attemptNum, attemptData, studentId } = arg;

        return {
          url: `exams/${subject}/${examId}/${questionId}/mark`,
          body: {
            studentId,
            attempt: attemptNum,
            ...attemptData,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamAttempts',
        id: `EXAM#${arg.subject}#${arg.examId}#${arg.attemptNum}#${arg.studentId}`,
      },
      {
        type: 'ExamAttemptsByMarker',
        id: result?.data?.markerId,
      }],
    }),
    submitExam: build.mutation<any,
    { subject: string,
      examId: string,
      attemptNum: number,
      studentId: string }>({
      query(arg) {
        const { subject, examId, attemptNum, studentId } = arg;

        return {
          url: `exams/${subject}/${examId}/submit`,
          body: {
            studentId,
            attempt: attemptNum,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamAttempts',
        id: `EXAM#${arg.subject}#${arg.examId}#${arg.attemptNum}#${arg.studentId}`,
      }, {
        type: 'ExamAttemptsByMarker',
        id: result?.data?.markerId,
      }],
    }),
    getExamAttempt: build.query<CognitoAPIResponse<ExamAttempt>, {
      subject: string, examId: string, studentId: string, attemptNum: number,
    }>({
      query(arg) {
        const { subject, examId, studentId, attemptNum } = arg;

        return {
          url: `exams/${subject}/${examId}/attempt`,
          params: {
            studentId,
            attempt: attemptNum,
          },
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) =>
        [{
          type: 'ExamAttempts',
          id: `EXAM#${arg.subject}#${arg.examId}#${arg.attemptNum}#${arg.studentId}`,
        }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) =>
          `${entry.subject}#${entry.examId}#${entry.student}#${entry.attemptNumber}`);
      },
    }),
    getExamAttemptsByUser: build.query<CognitoAPIResponse<ExamAttempt[]>, {
      student?: string, marker?: string, subject?: string, marked?: boolean
    }>({
      query(arg) {
        const { student, marker, subject, marked } = arg;

        return {
          url: `exams/${subject ? `${subject}/` : ''}attempts`,
          params: {
            student,
            marker,
            marked,
          },
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        ...(arg?.marker
          ? [{
            type: 'ExamAttemptsByMarker' as const,
            id: arg.marker,
          }]
          : [...(result?.data || []).map(({ markerId } : { markerId: string }) =>
            ({
              type: 'ExamAttemptsByMarker' as const,
              id: markerId,
            }))]),
      ],
      transformResponse(response: any) {
        return attachIds(response, (entry: any) =>
          `${entry.subject}#${entry.examId}#${entry.student}#${entry.attemptNumber}`);
      },
    }),
    getExamTypes: build.query<CognitoAPIResponse<ExamType[]>, {
      subject?: string,
    }>({
      query(arg) {
        const { subject } = arg;

        return {
          url: 'exams/types',
          method: 'GET',
          params: { subject },
        };
      },
      providesTags: (result, error, arg) => [
        arg.subject ? { type: 'ExamTypes', id: arg.subject } : 'ExamTypes',
      ],
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.type);
      },
    }),
    deleteExamAttempt: build.mutation<any, {
      subject: string, examId: string, studentId: string, attemptNum: number,
    }>({
      query(arg) {
        const { subject, examId, studentId, attemptNum } = arg;

        return {
          url: `exams/${subject}/${examId}/attempt`,
          params: {
            studentId,
            attempt: attemptNum,
          },
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) =>
        [{
          type: 'ExamAttempts',
          id: `EXAM#${arg.subject}#${arg.examId}#${arg.attemptNum}#${arg.studentId}`,
        }, {
          type: 'ExamAttemptsByMarker',
          id: result?.data?.markerId,
        }],
    }),
    getExamMarkingRequests: build.query<CognitoAPIResponse<ExamMarkingRequest[]>, {
      status?: string,
      marker?: string
    }>({
      query(arg) {
        const { status, marker } = arg;

        return {
          url: 'exams/requests',
          params: {
            status,
            marker,
          },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id } : { id: string }) =>
          ({
            type: 'ExamMarkingRequests' as const,
            id,
          })), 'ExamMarkingRequests']
        : ['ExamMarkingRequests']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.requestId);
      },
    }),
    getExamMarkingRequest: build.query<CognitoAPIResponse<ExamMarkingRequest>, {
      requestId: string
    }>({
      query(arg) {
        const { requestId } = arg;

        return {
          url: `exams/request/${requestId}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) => entry.requestId);
      },
    }),
    deleteExamMarkingRequest: build.mutation<any, {
      requestId: string
    }>({
      query(arg) {
        const { requestId } = arg;

        return {
          url: `exams/request/${requestId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }],
    }),
    updateExamMarkingRequest: build.mutation<any, {
      requestId: string, newAttributes: any
    }>({
      query(arg) {
        const { requestId, newAttributes } = arg;

        return {
          url: `exams/request/${requestId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }],
    }),
    approveExamMarkingRequest: build.mutation<any, {
      requestId: string, autoDrop?: boolean
    }>({
      query(arg) {
        const { requestId, autoDrop } = arg;

        return {
          url: `exams/request/${requestId}/approve`,
          method: 'PUT',
          body: {
            autoDrop,
          },
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }, {
        type: 'ExamAttemptsByMarker',
        id: result?.data?.markerId,
      }],
    }),
    unapproveExamMarkingRequest: build.mutation<any, {
      requestId: string,
    }>({
      query(arg) {
        const { requestId } = arg;

        return {
          url: `exams/request/${requestId}/unapprove`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }, {
        type: 'ExamAttemptsByMarker',
        id: result?.data?.markerId,
      }],
    }),
    acceptExamMarkingRequest: build.mutation<any, {
      requestId: string
    }>({
      query(arg) {
        const { requestId } = arg;

        return {
          url: `exams/request/${requestId}/accept`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }, {
        type: 'ExamAttemptsByMarker',
        id: result?.data?.markerId,
      }],
    }),
    dropExamMarkingRequest: build.mutation<any, {
      requestId: string
    }>({
      query(arg) {
        const { requestId } = arg;

        return {
          url: `exams/request/${requestId}/drop`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{
        type: 'ExamMarkingRequests',
        id: arg.requestId,
      }],
    }),
    getExamAttemptFileUrl: build.query<any, {
      subject: string, examId: string, studentId: string, attemptNum: number,
    }>({
      query(arg) {
        const { subject, examId, studentId, attemptNum } = arg;

        return {
          url: `exams/${subject}/${examId}/attempt/fileUrl`,
          params: {
            studentId,
            attempt: attemptNum,
          },
          method: 'GET',
        };
      },
      // Do not provide tags as download URL should be re-generated each time
    }),
    getExamMarkingRequestFileUrl: build.query<any, {
      requestId: string
    }>({
      query(arg) {
        const { requestId } = arg;

        return {
          url: `exams/request/${requestId}/fileUrl`,
          method: 'GET',
        };
      },
      // Do not provide tags as download URL should be re-generated each time
    }),
  }),
});

export const {
  useGetExamsQuery,
  useUpdateExamMutation,
  useDeleteExamMutation,
  useDeleteExamAttemptMutation,
  useRemoveQuestionMutation,
  useGetExamQuery,
  useGetQuestionQuery,
  useUpdateQuestionMutation,
  useNewExamMutation,
  useGetExamTypesQuery,
  useSubmitExamMutation,
  useAddQuestionMutation,
  useMarkQuestionMutation,
  useGetExamAttemptQuery,
  useGetExamAttemptsByUserQuery,
  useGetExamMarkingRequestsQuery,
  useGetExamMarkingRequestQuery,
  useDeleteExamMarkingRequestMutation,
  useUpdateExamMarkingRequestMutation,
  useApproveExamMarkingRequestMutation,
  useUnapproveExamMarkingRequestMutation,
  useAcceptExamMarkingRequestMutation,
  useDropExamMarkingRequestMutation,
  useLazyGetExamAttemptFileUrlQuery,
  useLazyGetExamMarkingRequestFileUrlQuery,
} = examsApi;
